//@flow
import React, { Component, Fragment } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import getter from 'dibs-object-getter';
import classNames from 'classnames';

import { profileAddressMutation } from '../mutations/profileAddress';
import { zipCodeLookup } from '../helpers/zipCodeLookup';
import { logError } from '../../helpers';

import { Tooltip } from 'dibs-elements/exports/Tooltip';
import { BasicSelect } from 'dibs-elements/exports/BasicSelect';
import { Input } from 'dibs-elements/exports/Input';
import { Button } from 'dibs-elements/exports/Button';

import LocationPin from 'dibs-icons/exports/LocationPin';
import Pen from 'dibs-icons/exports/Pen';

import styles from './ProfileLocation.scss';

import type { ProfileLocation_tradeFirm as TradeFirmType } from './__generated__/ProfileLocation_tradeFirm.graphql';
import type { ProfileLocation_geography as Geography } from './__generated__/ProfileLocation_geography.graphql';
import type { RelayProp } from 'react-relay';
import type { IntlShape } from 'react-intl';

type Option = { value: string | number, label: string };

const messages = defineMessages({
    countryPlaceholder: {
        id: 'abt.designProfile.header.location.countryPlaceholder',
        defaultMessage: 'Country',
    },
    zipCodePlaceholder: {
        id: 'abt.designProfile.header.location.zipCodePlaceholder',
        defaultMessage: 'ZIP Code',
    },
    cityStatePlaceholder: {
        id: 'abt.designProfile.header.location.cityStatePlaceholder',
        defaultMessage: 'City/State',
    },
});

type LocationData = {
    +city?: ?string,
    +state?: ?string,
    +country?: ?string,
};

type LocationOption = Option & { data: LocationData };

type Props = {
    tradeFirm: ?TradeFirmType,
    geography: ?Geography,
    relay?: RelayProp,
    isEditMode: boolean,
    intl: IntlShape,
};

type State = {
    isEditing: boolean,
    country: ?string,
    zipCode: ?string,
    city: ?string,
    state: ?string,
    isSaving: boolean,
    cityStateDisplay: ?string | ?number,
    locations: Array<LocationOption>,
};

export class ProfileLocationComponent extends Component<Props, State> {
    static getLabelString = (data: LocationData): string => {
        const city = data.city || '';
        const state = data.state || '';
        const country = data.country || '';

        return `${city}, ${country === 'US' ? state : country}`;
    };

    static getCityStateHash = (data: LocationData): string => {
        const replaceRegex = /\W+/;

        const city = data.city || '';
        const state = data.state || '';
        const country = data.country || '';

        return `${city.replace(replaceRegex, '_')}-${state.replace(replaceRegex, '_')}-${country}`;
    };

    constructor(props: Props) {
        super(props);

        const address =
            getter(this.props.tradeFirm)
                .at('publicProfile')
                .value('address') || {};

        const country = address.country || '';
        const zipCode = address.zipCode || '';
        const city = address.city || '';
        const state = address.state || '';

        const cityStateDisplay = ProfileLocationComponent.getCityStateHash(address);
        const defaultLocation = {
            value: cityStateDisplay,
            label: ProfileLocationComponent.getLabelString(address),
            data: { city, state, country },
        };

        this.state = {
            isEditing: false,
            country,
            zipCode,
            city,
            state,
            isSaving: false,
            cityStateDisplay,
            locations: [defaultLocation],
        };

        this.onSaveLocation = this.onSaveLocation.bind(this);
    }

    startEditing = () => {
        // istanbul ignore else
        if (this.props.isEditMode) {
            this.setState({ isEditing: true });
        }
    };

    closeEditor = () => {
        // istanbul ignore else
        if (this.props.isEditMode) {
            this.setState({ isEditing: false });
        }
    };

    cancelEditor = () => {
        // istanbul ignore else
        if (this.props.isEditMode) {
            const getAddress = getter(this.props.tradeFirm)
                .at('publicProfile')
                .at('address');

            const country = getAddress.value('country');
            const zipCode = getAddress.value('zipCode');

            this.setState({ country, zipCode, isEditing: false });
        }
    };

    onSaveLocation = () => {
        const firmId = getter(this.props.tradeFirm).value('serviceId') || '';
        const { zipCode, city, state, country } = this.state;

        if (this.props.relay && firmId && zipCode && country) {
            const { environment } = this.props.relay;
            this.setState({ isSaving: true }, () => {
                const args = {
                    firmId,
                    zipCode,
                    country,
                    city,
                    state,
                };
                profileAddressMutation(environment, args)
                    .then(() => {
                        this.setState({ isSaving: false, isEditing: false });
                    })
                    .catch((e: Error) => {
                        logError(e, 'Error on saving location');
                        this.setState({ isSaving: false });
                    });
            });
        }
    };

    onSelectCountry = (e: SyntheticInputEvent<HTMLSelectElement>) => {
        // istanbul ignore else
        if (this.props.isEditMode && e) {
            this.setState({ country: e.target.value });
        }
    };

    onSelectCityState = (e: SyntheticInputEvent<HTMLSelectElement>) => {
        // istanbul ignore else
        if (e) {
            const location = this.state.locations.find(loc => {
                return loc.value === e.target.value;
            });

            if (location) {
                this.setState({
                    cityStateDisplay: location.value,
                    city: location.data.city,
                    state: location.data.state,
                });
            }
        }
    };

    onChangeZipCode = (event: SyntheticInputEvent<HTMLInputElement>) => {
        // istanbul ignore else
        if (this.props.isEditMode) {
            // istanbul ignore next
            const zipCode = event && event.target ? event.target.value : '';
            this.setState({ zipCode }, () => {
                // istanbul ignore else
                if (this.props.relay && this.state.country && zipCode.length >= 3) {
                    zipCodeLookup(this.props.relay.environment, {
                        country: this.state.country,
                        zipCode,
                    })
                        .then(response => {
                            const results =
                                getter(response)
                                    .at('viewer')
                                    .value('zipCode') || [];

                            const locations = results.map(result => {
                                const label = ProfileLocationComponent.getLabelString(result);
                                const value = ProfileLocationComponent.getCityStateHash(result);

                                return {
                                    label,
                                    value,
                                    data: result,
                                };
                            });

                            let cityStateDisplay = this.state.cityStateDisplay;
                            let city = this.state.city;
                            let state = this.state.state;
                            const [firstResult] = locations;

                            if (firstResult && firstResult.value) {
                                cityStateDisplay = firstResult.value;
                                city = firstResult.data.city;
                                state = firstResult.data.state;
                            }

                            this.setState({ locations, cityStateDisplay, city, state });
                        })
                        .catch(e => {
                            logError(e, 'Error encountered on zip code lookup');
                        });
                }
            });
        }
    };

    render() {
        const getAddress = getter(this.props.tradeFirm)
            .at('publicProfile')
            .at('address');
        const city = getAddress.value('city') || '';
        const state = getAddress.value('state') || '';
        const country = getAddress.value('country') || '';

        const countries = (getter(this.props.geography).value('countries') || [])
            .map(_country => ({
                value: (_country && _country.abbreviation) || '',
                label: (_country && _country.name) || '',
            }))
            .filter((_country: Option) => _country && _country.value && _country.label);

        const containerClass = classNames(styles.container, {
            [styles.edit]: this.props.isEditMode,
        });

        let display = (
            <FormattedMessage
                id="abt.designProfile.header.location"
                defaultMessage="{city}, {stateOrCountry}"
                values={{
                    city,
                    stateOrCountry: country === 'US' ? state : country,
                }}
            />
        );

        // istanbul ignore next
        if (this.props.isEditMode && (!city || (!country && !state))) {
            display = (
                <FormattedMessage
                    id="abt.designProfile.header.addLocation"
                    defaultMessage="Add Your Firm's Location"
                />
            );
        }

        return (
            <div className={containerClass}>
                <div
                    className={styles.wrapper}
                    onClick={this.startEditing}
                    data-tn="location-display"
                >
                    <LocationPin className={styles.pin} />
                    <span className={styles.labelText}>{display}</span>
                    {this.props.isEditMode && (
                        <Fragment>
                            <Pen className={styles.pen} />
                            <Tooltip
                                isVisible={this.state.isEditing}
                                direction="bottom"
                                closeOnOutsideClick
                                onClose={this.closeEditor}
                            >
                                <div className={styles.inputWrapper}>
                                    <BasicSelect
                                        name="country-select"
                                        value={this.state.country}
                                        onChange={this.onSelectCountry}
                                        dataTn="country-select"
                                        options={countries}
                                        placeholder={this.props.intl.formatMessage(
                                            messages.countryPlaceholder
                                        )}
                                        hasAnimatedPlaceholder
                                    />
                                </div>

                                <div className={styles.inputWrapper}>
                                    <Input
                                        dataTn="zipCode-input"
                                        value={this.state.zipCode || ''}
                                        onChange={this.onChangeZipCode}
                                        placeholder={this.props.intl.formatMessage(
                                            messages.zipCodePlaceholder
                                        )}
                                        hasAnimatedPlaceholder
                                    />
                                </div>

                                <div className={styles.inputWrapper}>
                                    <BasicSelect
                                        name="city-state-select"
                                        value={this.state.cityStateDisplay}
                                        onChange={this.onSelectCityState}
                                        options={this.state.locations.map(({ label, value }) => ({
                                            label,
                                            value,
                                        }))}
                                        dataTn="city-state-select"
                                        placeholder={this.props.intl.formatMessage(
                                            messages.cityStatePlaceholder
                                        )}
                                        hasAnimatedPlaceholder
                                    />
                                </div>
                                <div className={styles.buttonRow}>
                                    <Button
                                        onClick={this.cancelEditor}
                                        dataTn="location-cancel"
                                        type="secondary"
                                        className={styles.button}
                                        size="medium"
                                        disabled={this.state.isSaving}
                                    >
                                        <FormattedMessage
                                            id="abt.designProfile.header.location.cancel"
                                            defaultMessage="Cancel"
                                        />
                                    </Button>
                                    <Button
                                        onClick={this.onSaveLocation}
                                        dataTn="location-save"
                                        className={styles.button}
                                        size="medium"
                                        disabled={
                                            this.state.isSaving ||
                                            !this.state.country ||
                                            !this.state.zipCode
                                        }
                                    >
                                        <FormattedMessage
                                            id="abt.designProfile.header.location.save"
                                            defaultMessage="Save"
                                        />
                                    </Button>
                                </div>
                            </Tooltip>
                        </Fragment>
                    )}
                </div>
            </div>
        );
    }
}

export const ProfileLocation = createFragmentContainer(injectIntl(ProfileLocationComponent), {
    geography: graphql`
        fragment ProfileLocation_geography on Geography {
            countries {
                name
                abbreviation
            }
        }
    `,
    tradeFirm: graphql`
        fragment ProfileLocation_tradeFirm on TradeFirmType {
            serviceId
            publicProfile {
                address {
                    city
                    state
                    country
                    zipCode
                }
            }
        }
    `,
});
